import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { NavLink } from "react-router-dom";
import "./landingpage.scss";
import {
  HeroBoardArt,
  BurndownArt,
  CollaborationArt,
  PlanningArt,
  IconBoard,
  IconBacklog,
  IconChart,
  IconUsers,
  IconShield,
  IconBell,
  IconCheck,
  IconArrow,
  IconChevron,
} from "./landingArt";

const SLIDE_MS = 5000;

/* Fixed so the motes don't re-scatter on each render. */
const MOTES = [
  { left: 8, top: 72, delay: 0, duration: 15 },
  { left: 18, top: 88, delay: 2.5, duration: 18 },
  { left: 29, top: 64, delay: 5, duration: 14 },
  { left: 41, top: 92, delay: 1.2, duration: 20 },
  { left: 55, top: 70, delay: 6.5, duration: 16 },
  { left: 67, top: 86, delay: 3.4, duration: 19 },
  { left: 79, top: 66, delay: 8, duration: 15 },
  { left: 90, top: 90, delay: 4.6, duration: 17 },
];

/* Hero carousel. Product-voice lines rather than attributed quotes — a real
   quotation needs a real person behind it. */
const SLIDES = [
  {
    eyebrow: "Agile project management",
    quote: (
      <>
        Ship every sprint with{" "}
        <span className="lp-gradient-text">everything in one place</span>
      </>
    ),
    lead:
      "Board, backlog, issues and reports in one workspace — so the team always knows what is in flight, what is blocked and what is done.",
    art: <HeroBoardArt />,
    label: "Sprint board",
  },
  {
    eyebrow: "Plan with intent",
    quote: (
      <>
        A backlog that becomes{" "}
        <span className="lp-gradient-text">a real plan</span>
      </>
    ),
    lead:
      "Estimate, prioritise and group the work before you commit to it — then start the sprint in a single step.",
    art: <PlanningArt />,
    label: "Backlog planning",
  },
  {
    eyebrow: "Track honestly",
    quote: (
      <>
        Know where the sprint{" "}
        <span className="lp-gradient-text">really stands</span>
      </>
    ),
    lead:
      "Remaining work against the ideal line, updated as issues move. A slipping sprint shows up days before the review.",
    art: <BurndownArt />,
    label: "Burn-down",
  },
  {
    eyebrow: "Decide together",
    quote: (
      <>
        Keep the conversation{" "}
        <span className="lp-gradient-text">on the work</span>
      </>
    ),
    lead:
      "Comments, replies and a full change history live on the issue itself — no hunting through chat to find why something changed.",
    art: <CollaborationArt />,
    label: "Collaboration",
  },
];

const FEATURES = [
  {
    icon: <IconBoard />,
    tone: "is-green",
    title: "Sprint board",
    body: "Drag issues across your own workflow columns. Story points, priority and assignee stay visible at a glance.",
  },
  {
    icon: <IconBacklog />,
    tone: "is-blue",
    title: "Backlog planning",
    body: "Groom the backlog, size the work and pull it into a sprint when the team is ready to commit.",
  },
  {
    icon: <IconChart />,
    tone: "is-violet",
    title: "Burn-down & reports",
    body: "See remaining work against the ideal line, sprint over sprint, so slippage shows up early.",
  },
  {
    icon: <IconUsers />,
    tone: "is-amber",
    title: "Team collaboration",
    body: "Comments, replies and mentions live on the issue, so the decision sits next to the work.",
  },
  {
    icon: <IconShield />,
    tone: "is-blue",
    title: "Roles & access",
    body: "Project managers, leads, scrum masters and developers each get the permissions they need.",
  },
  {
    icon: <IconBell />,
    tone: "is-green",
    title: "Stay in the loop",
    body: "Assignments, status changes and replies reach the right people by notification and email.",
  },
];

const STEPS = [
  {
    title: "Create your project",
    body: "Set up a project, invite the team and define the workflow columns that match how you actually work.",
  },
  {
    title: "Plan a sprint",
    body: "Add issues to the backlog, estimate them, then start a sprint with a clear scope and end date.",
  },
  {
    title: "Ship and review",
    body: "Track progress on the board, watch the burn-down, and close the sprint with everything accounted for.",
  },
];

const LandingPage = () => {
  const rootRef = useRef(null);
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((next) => {
    setSlide((next + SLIDES.length) % SLIDES.length);
  }, []);

  /* Autoplay, held while the viewer hovers or keyboard-focuses the hero, and
     switched off entirely for anyone who asked for reduced motion. */
  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (paused || prefersReduced) return undefined;

    const timer = setTimeout(
      () => setSlide((current) => (current + 1) % SLIDES.length),
      SLIDE_MS
    );
    return () => clearTimeout(timer);
  }, [slide, paused]);

  /* Scroll reveal. Falls back to simply showing everything when
     IntersectionObserver is unavailable or motion is reduced. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = root.querySelectorAll("[data-reveal]");
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      targets.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = Number(entry.target.dataset.revealDelay || 0);
          setTimeout(() => entry.target.classList.add("is-visible"), delay);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <Box className="pj-landing" ref={rootRef}>
      {/* ------------------------------------------------------------ hero */}
      <section
        className="lp-hero"
        aria-roledescription="carousel"
        aria-label="What Projam does"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        <div className="lp-hero-glow" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>

        <div className="lp-motes" aria-hidden="true">
          {MOTES.map((m, i) => (
            <i
              key={i}
              style={{
                left: `${m.left}%`,
                top: `${m.top}%`,
                animationDelay: `${m.delay}s`,
                animationDuration: `${m.duration}s`,
              }}
            />
          ))}
        </div>

        <div className="lp-wrap">
          <div className="lp-hero-grid">
            {/* ---- copy ---- */}
            <div className="lp-hero-copy" data-reveal>
              <div className="lp-slides" aria-live="polite">
                {SLIDES.map((item, i) => (
                  <div
                    className={`lp-slide ${i === slide ? "is-active" : ""}`}
                    key={item.label}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${i + 1} of ${SLIDES.length}: ${item.label}`}
                    aria-hidden={i !== slide}
                  >
                    <span className="lp-eyebrow">{item.eyebrow}</span>
                    <h1 className="lp-quote">{item.quote}</h1>
                    <p className="lp-lead">{item.lead}</p>
                  </div>
                ))}
              </div>

              {/* CTAs stay put while the copy rotates, so the target never moves */}
              <div className="lp-hero-cta">
                <NavLink to="/register" className="lp-btn lp-btn--primary">
                  Get started free <IconArrow />
                </NavLink>
                <NavLink to="/login" className="lp-btn lp-btn--ghost">
                  Sign in
                </NavLink>
              </div>

              <div className="lp-carousel-bar">
                <div className="lp-dots">
                  {SLIDES.map((item, i) => (
                    <button
                      type="button"
                      key={item.label}
                      className={`lp-dot ${i === slide ? "is-active" : ""}`}
                      style={
                        i === slide && !paused
                          ? { "--lp-slide-ms": `${SLIDE_MS}ms` }
                          : undefined
                      }
                      onClick={() => goTo(i)}
                      aria-label={`Show slide ${i + 1}: ${item.label}`}
                      aria-current={i === slide}
                    />
                  ))}
                </div>

                <div className="lp-arrows">
                  <button
                    type="button"
                    className="lp-arrow"
                    onClick={() => goTo(slide - 1)}
                    aria-label="Previous slide"
                  >
                    <IconChevron dir="left" />
                  </button>
                  <button
                    type="button"
                    className="lp-arrow"
                    onClick={() => goTo(slide + 1)}
                    aria-label="Next slide"
                  >
                    <IconChevron dir="right" />
                  </button>
                </div>
              </div>
            </div>

            {/* ---- animated art ---- */}
            <div className="lp-hero-stage" data-reveal data-reveal-delay="140">
              {SLIDES.map((item, i) => (
                <div
                  className={`lp-stage-slide ${i === slide ? "is-active" : ""}`}
                  key={item.label}
                  aria-hidden={i !== slide}
                >
                  {/* remounting on change replays the SVG's own animation */}
                  {i === slide ? (
                    <React.Fragment key={`art-${slide}`}>{item.art}</React.Fragment>
                  ) : (
                    item.art
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- features */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              What you get
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              Everything an agile team needs
            </h2>
            <p className="lp-lead" data-reveal data-reveal-delay="140">
              Purpose-built for scrum teams — not a general task list bent into
              the shape of one.
            </p>
          </div>

          <div className="lp-grid">
            {FEATURES.map((feature, i) => (
              <article
                className="lp-card"
                key={feature.title}
                data-reveal
                data-reveal-delay={i * 70}
              >
                <div className={`lp-card-icon ${feature.tone}`}>
                  {feature.icon}
                </div>
                <h3 className="lp-h3">{feature.title}</h3>
                <p className="lp-body">{feature.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- showcase */}
      <section className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-wrap">
          <div className="lp-showcase">
            <div>
              <span className="lp-eyebrow" data-reveal>
                Plan
              </span>
              <h2 className="lp-h2" data-reveal data-reveal-delay="70">
                A backlog that turns into a plan
              </h2>
              <p className="lp-lead" data-reveal data-reveal-delay="120">
                Estimate, prioritise and group work before you commit to it —
                then start the sprint in a single step.
              </p>
              <ul className="lp-checks" data-reveal data-reveal-delay="180">
                {[
                  "Story point estimates and priority on every issue",
                  "Epics to group work that spans sprints",
                  "Start and complete sprints with a clear scope",
                ].map((item) => (
                  <li key={item}>
                    <span className="lp-check">
                      <IconCheck />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lp-showcase-art" data-reveal data-reveal-delay="120">
              <PlanningArt />
            </div>
          </div>

          <div className="lp-showcase is-flipped">
            <div>
              <span className="lp-eyebrow" data-reveal>
                Track
              </span>
              <h2 className="lp-h2" data-reveal data-reveal-delay="70">
                Know where the sprint really stands
              </h2>
              <p className="lp-lead" data-reveal data-reveal-delay="120">
                The burn-down compares remaining work against the ideal line,
                so a slipping sprint is obvious days before the review.
              </p>
              <ul className="lp-checks" data-reveal data-reveal-delay="180">
                {[
                  "Burn-down with an ideal-versus-actual guide",
                  "Issue breakdown by status and assignee",
                  "Sprint-over-sprint history",
                ].map((item) => (
                  <li key={item}>
                    <span className="lp-check">
                      <IconCheck />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lp-showcase-art" data-reveal data-reveal-delay="120">
              <BurndownArt />
            </div>
          </div>

          <div className="lp-showcase">
            <div>
              <span className="lp-eyebrow" data-reveal>
                Collaborate
              </span>
              <h2 className="lp-h2" data-reveal data-reveal-delay="70">
                Keep the conversation on the work
              </h2>
              <p className="lp-lead" data-reveal data-reveal-delay="120">
                Comments, replies and a full history live on the issue itself —
                no hunting through chat to find why something changed.
              </p>
              <ul className="lp-checks" data-reveal data-reveal-delay="180">
                {[
                  "Threaded comments and replies on every issue",
                  "A complete change history, field by field",
                  "Notifications and email for what matters to you",
                ].map((item) => (
                  <li key={item}>
                    <span className="lp-check">
                      <IconCheck />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lp-showcase-art" data-reveal data-reveal-delay="120">
              <CollaborationArt />
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- steps */}
      <section className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              How it works
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              Up and running in three steps
            </h2>
          </div>

          <div className="lp-steps">
            {STEPS.map((step, i) => (
              <article
                className="lp-step"
                key={step.title}
                data-reveal
                data-reveal-delay={i * 90}
              >
                <span className="lp-step-num">{i + 1}</span>
                <h3 className="lp-h3">{step.title}</h3>
                <p className="lp-body">{step.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-wrap">
          <div className="lp-cta" data-reveal>
            <h2 className="lp-h2">Start your next sprint on Projam</h2>
            <p>
              Create a project, invite your team and plan your first sprint in
              minutes.
            </p>
            <div className="lp-cta-actions">
              <NavLink to="/register" className="lp-btn lp-btn--white">
                Get started free <IconArrow />
              </NavLink>
              <NavLink to="/login" className="lp-btn lp-btn--light">
                Sign in
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};

export default LandingPage;
